import { useCallback, useEffect, useState } from 'react';

import { useLazyQuery } from '@apollo/client';

import { getClient } from 'lane-shared/apollo';
import { pagination } from 'lane-shared/helpers/constants';

import { multiBuildingServiceRequestsQuery } from 'graphql-queries';

const FETCH_POLICY = 'network-only';

export type MultiBuildingFilterParams = {
  companies?: string[];
  categories?: string[];
  statuses?: string[];
  issues?: string[];
  locations?: string[];
  equipments?: string[];
  assignees?: string[];
  assigneeGroups?: string[];
  isBillable?: boolean;
  isEscalated?: boolean;
  createDateStart?: Date;
  createDateEnd?: Date;
};

export type MultiBuildingQueryOptions = {
  perPage?: number;
  searchParams?: MultiBuildingFilterParams;
  search?: string;
};

const EMPTY_FILTER: MultiBuildingFilterParams = {};

function buildFilter(searchParams: MultiBuildingFilterParams): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(searchParams).filter(([, value]) => value !== undefined)
  );
}

export const useMultiBuildingSearchServiceRequestsQuery = (
  channelIds: string[],
  options: MultiBuildingQueryOptions = {}
) => {
  const {
    perPage = pagination.perPage,
    searchParams = EMPTY_FILTER,
    search = '',
  } = options;

  const [page, setPage] = useState(1);

  const [isFetchingMore, setIsFetchingMore] = useState(false);

  const [fetchServiceRequests, { data, loading, error, fetchMore, called }] =
    useLazyQuery(multiBuildingServiceRequestsQuery, {
      fetchPolicy: FETCH_POLICY,
      client: getClient(),
    });

  const buildVariables = useCallback((startPage: number) => ({
    channelIds: channelIds || [],
    pagination: {
      start: (startPage - 1) * perPage,
      perPage,
    },
    ...(search
      ? { search: { type: 'like', value: search } }
      : {}),
    filter: buildFilter(searchParams),
  }), [perPage, channelIds, search, searchParams]);

  // Fetch page 2+ — appends results to existing list
  useEffect(() => {
    if (!called || page === 1) {
      return;
    }

    const variables = buildVariables(page);

    setIsFetchingMore(true);
    fetchMore({
      variables,
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) {
          return prev;
        }

        const prevItems = prev.multiBuildingServiceRequests.serviceRequests;
        const newItems = fetchMoreResult.multiBuildingServiceRequests.serviceRequests;

        return {
          multiBuildingServiceRequests: {
            __typename: 'ServiceRequestSearchResults',
            pageInfo: {
              ...fetchMoreResult.multiBuildingServiceRequests.pageInfo,
              total: prev.multiBuildingServiceRequests.pageInfo.total,
            },
            serviceRequests: [...prevItems, ...newItems],
          },
        };
      },
    })
      .finally(() => setIsFetchingMore(false));
  }, [page, buildVariables, called, fetchMore]);

  // Reset to page 1 and fetch fresh when channels/search/filters change
  useEffect(() => {
    setPage(1);
    fetchServiceRequests({ variables: buildVariables(1) });
  }, [buildVariables, fetchServiceRequests]);

  const refetch = () => {
    if (!loading) {
      setPage(1);
      fetchServiceRequests({ variables: buildVariables(1) });
    }
  };

  const items = data?.multiBuildingServiceRequests?.serviceRequests || [];
  const pageInfo = data?.multiBuildingServiceRequests?.pageInfo || {
    total: 0,
    start: 0,
    perPage: 0,
  };

  const isInitialLoading = page === 1 && loading && items.length === 0;

  return {
    items,
    pageInfo,
    loading,
    isInitialLoading,
    isFetchingMore,
    error,
    page,
    setPage,
    refetch,
  };
};
